import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Film, Loader2, Plus, Upload, Trash2, Scissors, Play, Square,
  AlertTriangle, GripVertical, Clapperboard, Library, Layers, Captions,
} from 'lucide-react';
import { toast } from 'sonner';
import { drawVideoContain, CAPTION_PRESETS } from '../lib/captionPresets';
import { useVideoRender } from '../context/VideoRenderContext';
import { getVideoCapabilities } from '../lib/videoSupport';

const FORMAT_DIMS = { '9:16': [1080, 1920], '16:9': [1920, 1080], '1:1': [1080, 1080], '4:5': [1080, 1350] };
const MAX_CLIPS = 12;
const uid = () => Math.random().toString(36).slice(2, 9);
const fmt = (s) => `${(s || 0).toFixed(1)}s`;

const BACKEND_URL = (() => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  return (!raw || raw === 'undefined') ? '' : raw;
})();
const mediaUrl = (m) => (m?.url ? (m.url.startsWith('http') ? m.url : `${BACKEND_URL}${m.url}`) : null);

const loadMeta = (url) => new Promise((resolve) => {
  const v = document.createElement('video');
  v.preload = 'metadata';
  v.onloadedmetadata = () => resolve(isFinite(v.duration) ? v.duration : 0);
  v.onerror = () => resolve(0);
  v.src = url;
});

/** Phase 3a — A-roll multi-clip timeline: add clips, trim, split, reorder,
 *  delete, then render the sequence to one MP4 (in-browser) and save/attach/post. */
export const VideoTimelineModal = ({ open, onClose, onSaved }) => {
  const { api } = useAuth();
  const { enqueue } = useVideoRender();
  const caps = getVideoCapabilities();
  const supported = caps.canBurnIn; // sequential canvas render needs MediaRecorder+captureStream

  const [clips, setClips] = useState([]);          // {id,name,url,blob?,duration,trimStart,trimEnd}
  const [selectedId, setSelectedId] = useState(null);
  const [splitAt, setSplitAt] = useState(0);
  const [format, setFormat] = useState('9:16');
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [library, setLibrary] = useState([]);
  const [showLib, setShowLib] = useState(false);
  const [posts, setPosts] = useState([]);
  const [targetPostId, setTargetPostId] = useState('');
  const [broll, setBroll] = useState([]);          // {id,name,url,blob?,duration,trimStart,trimEnd,start}
  const [captionTimeline, setCaptionTimeline] = useState(false);
  const [timelinePreset, setTimelinePreset] = useState('bold_pop');
  const dragIdx = useRef(null);
  const previewCanvasRef = useRef(null);
  const stopPreviewRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setClips([]); setSelectedId(null); setTargetPostId(''); setShowLib(false);
      setBroll([]); setCaptionTimeline(false);
      stopPreviewRef.current = true;
      return;
    }
    api.get('/video/library').then((r) => setLibrary((r.data.projects || []).filter((p) => p.final_media))).catch(() => {});
    api.get('/posts?limit=200').then((r) => setPosts(r.data.posts || [])).catch(() => {});
  }, [open, api]);

  const selected = clips.find((c) => c.id === selectedId) || null;
  const totalDuration = clips.reduce((a, c) => a + Math.max(0, (c.trimEnd || c.duration) - (c.trimStart || 0)), 0);

  const addClip = async (url, name, blob) => {
    if (clips.length >= MAX_CLIPS) { toast.error(`Max ${MAX_CLIPS} clips`); return; }
    const duration = await loadMeta(url);
    const clip = { id: uid(), name, url, blob, duration, trimStart: 0, trimEnd: duration };
    setClips((prev) => [...prev, clip]);
    setSelectedId(clip.id);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await addClip(URL.createObjectURL(file), file.name.slice(0, 24), file);
    e.target.value = '';
  };

  const addFromLibrary = async (proj) => {
    const url = mediaUrl(proj.final_media);
    if (!url) return;
    await addClip(url, (proj.name || 'Clip').slice(0, 24));
    setShowLib(false);
  };

  const updateClip = (id, patch) => setClips((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeClip = (id) => {
    setClips((prev) => prev.filter((c) => c.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // ---- B-roll overlay (cutaway; A-roll audio continues) ----
  const addBroll = async (url, name, blob) => {
    const duration = await loadMeta(url);
    setBroll((prev) => [...prev, { id: uid(), name, url, blob, duration, trimStart: 0, trimEnd: duration, start: 0 }]);
  };
  const onBrollUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await addBroll(URL.createObjectURL(file), file.name.slice(0, 22), file);
    e.target.value = '';
  };
  const updateBroll = (id, patch) => setBroll((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const removeBroll = (id) => setBroll((prev) => prev.filter((b) => b.id !== id));

  const splitClip = () => {
    if (!selected) return;
    const s = selected.trimStart || 0;
    const e = selected.trimEnd || selected.duration;
    const at = Math.min(Math.max(splitAt, s + 0.2), e - 0.2);
    if (!(at > s && at < e)) { toast.error('Pick a split point inside the clip'); return; }
    const a = { ...selected, id: uid(), trimEnd: at };
    const b = { ...selected, id: uid(), trimStart: at, name: `${selected.name} (2)` };
    setClips((prev) => prev.flatMap((c) => (c.id === selected.id ? [a, b] : [c])));
    setSelectedId(a.id);
    toast.success('Clip split');
  };

  // drag reorder
  const onDragStart = (i) => { dragIdx.current = i; };
  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    setClips((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIdx.current = null;
  };

  // ----- sequential player (preview only; final render runs in the background) -----
  const playSequence = async (ctx, W, H, { audioDest, audioCtx } = {}) => {
    for (const clip of clips) {
      if (stopPreviewRef.current) break;
      const v = document.createElement('video');
      v.src = clip.url; v.playsInline = true; v.crossOrigin = 'anonymous';
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res, rej) => { v.onloadeddata = res; v.onerror = () => rej(new Error('load')); });
      let node = null;
      if (audioCtx && audioDest) {
        try { node = audioCtx.createMediaElementSource(v); node.connect(audioDest); } catch (_) { node = null; }
      }
      const start = clip.trimStart || 0;
      const end = (clip.trimEnd && clip.trimEnd > start) ? clip.trimEnd : (clip.duration || v.duration || 0);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((res) => { v.currentTime = start; v.onseeked = res; });
      // eslint-disable-next-line no-await-in-loop
      await v.play().catch(() => {});
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        const draw = () => {
          if (stopPreviewRef.current) { v.pause(); resolve(); return; }
          const t = v.currentTime;
          if (v.ended || (end && t >= end)) { v.pause(); resolve(); return; }
          drawVideoContain(ctx, v, W, H);
          requestAnimationFrame(draw);
        };
        requestAnimationFrame(draw);
      });
      if (node) { try { node.disconnect(); } catch (_) { /* noop */ } }
    }
  };

  const preview = async () => {
    if (!clips.length) return;
    setPreviewing(true); stopPreviewRef.current = false;
    const [W, H] = FORMAT_DIMS[format];
    const cvs = previewCanvasRef.current;
    cvs.width = W; cvs.height = H;
    const ctx = cvs.getContext('2d');
    try { await playSequence(ctx, W, H); } finally { setPreviewing(false); }
  };

  const finish = async () => {
    if (clips.length < 1) { toast.error('Add at least one clip'); return; }
    setBusy(true); setBusyMsg('Creating project…');
    try {
      const proj = (await api.post('/video/projects', { name: `Timeline (${clips.length} clips)` })).data;
      // Snapshot just what the renderer needs (urls/blobs survive modal close).
      const jobClips = clips.map((c) => ({ url: c.url, trimStart: c.trimStart, trimEnd: c.trimEnd, duration: c.duration }));
      const jobBroll = broll.map((b) => ({ url: b.url, start: b.start, trimStart: b.trimStart, trimEnd: b.trimEnd, duration: b.duration }));
      enqueue({
        kind: 'timeline',
        projectId: proj.id,
        clips: jobClips,
        broll: jobBroll,
        captions: captionTimeline,
        presetKey: timelinePreset,
        format,
        totalDuration,
        targetPostId,
        label: targetPostId ? 'Rendering timeline & attaching' : 'Rendering timeline',
      });
      toast.success('Rendering in the background — you can keep working');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the timeline render');
    } finally { setBusy(false); setBusyMsg(''); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="video-timeline-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clapperboard className="w-5 h-5 text-indigo-600" /> Timeline Editor <span className="text-[11px] font-normal text-zinc-400">· A-roll</span></DialogTitle>
        </DialogHeader>

        {!supported ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex items-start gap-2" data-testid="timeline-unsupported">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">The timeline editor renders in your browser and needs <strong>Chrome or Edge on desktop</strong>. Please switch browsers to use it.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Add clips */}
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white cursor-pointer hover:bg-indigo-700" data-testid="timeline-upload-label">
                <Upload className="w-4 h-4 mr-1.5" /> Add clip
                <input type="file" accept="video/*" className="hidden" onChange={onUpload} data-testid="timeline-upload-input" />
              </label>
              <Button variant="outline" size="sm" onClick={() => setShowLib((s) => !s)} data-testid="timeline-add-from-library">
                <Library className="w-4 h-4 mr-1.5" /> From library
              </Button>
              <span className="text-[11px] text-zinc-400 ml-auto">{clips.length}/{MAX_CLIPS} clips · total {fmt(totalDuration)}</span>
            </div>

            {showLib && (
              <div className="rounded-lg border border-zinc-200 p-2 max-h-40 overflow-y-auto space-y-1" data-testid="timeline-library-list">
                {library.length === 0 ? (
                  <p className="text-[11px] text-zinc-400 p-2">No rendered videos in your library yet.</p>
                ) : library.map((p) => (
                  <button key={p.id} type="button" onClick={() => addFromLibrary(p)} className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-zinc-50 flex items-center gap-2" data-testid={`timeline-lib-${p.id}`}>
                    <Film className="w-3.5 h-3.5 text-zinc-400" /> {p.name || 'Untitled'} <span className="text-zinc-400 ml-auto">{p.final_media?.format || ''}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Timeline track */}
            {clips.length === 0 ? (
              <div className="rounded-lg border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-400" data-testid="timeline-empty">
                Add clips to build your sequence. Drag to reorder · select a clip to trim or split.
              </div>
            ) : (
              <div className="flex gap-2 overflow-x-auto py-1" data-testid="timeline-track">
                {clips.map((c, i) => {
                  const len = Math.max(0.1, (c.trimEnd || c.duration) - (c.trimStart || 0));
                  return (
                    <div
                      key={c.id}
                      draggable
                      onDragStart={() => onDragStart(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDrop(i)}
                      onClick={() => { setSelectedId(c.id); setSplitAt(((c.trimStart || 0) + (c.trimEnd || c.duration)) / 2); }}
                      className={`shrink-0 rounded-md border p-2 cursor-pointer select-none transition-colors ${selectedId === c.id ? 'border-indigo-500 ring-1 ring-indigo-500 bg-indigo-50/40' : 'border-zinc-200 hover:border-zinc-300'}`}
                      style={{ width: `${Math.min(180, Math.max(70, len * 14))}px` }}
                      data-testid={`timeline-clip-${c.id}`}
                    >
                      <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-1">
                        <GripVertical className="w-3 h-3" /> #{i + 1}
                      </div>
                      <div className="h-10 rounded bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center">
                        <Film className="w-4 h-4 text-white/80" />
                      </div>
                      <p className="text-[10px] text-zinc-700 mt-1 truncate">{c.name}</p>
                      <p className="text-[10px] text-zinc-400">{fmt(len)}</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Selected clip editor */}
            {selected && (
              <div className="rounded-lg border border-zinc-200 p-3 space-y-3" data-testid="timeline-clip-editor">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-zinc-800 truncate">{selected.name}</p>
                  <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => removeClip(selected.id)} data-testid="timeline-delete-clip">
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <Label className="text-[11px]">Trim start: {fmt(selected.trimStart)}</Label>
                    <input type="range" min={0} max={selected.duration} step={0.1} value={selected.trimStart}
                      onChange={(e) => updateClip(selected.id, { trimStart: Math.min(Number(e.target.value), (selected.trimEnd || selected.duration) - 0.2) })}
                      className="w-full accent-indigo-600" data-testid="timeline-trim-start" />
                  </div>
                  <div>
                    <Label className="text-[11px]">Trim end: {fmt(selected.trimEnd)}</Label>
                    <input type="range" min={0} max={selected.duration} step={0.1} value={selected.trimEnd}
                      onChange={(e) => updateClip(selected.id, { trimEnd: Math.max(Number(e.target.value), (selected.trimStart || 0) + 0.2) })}
                      className="w-full accent-indigo-600" data-testid="timeline-trim-end" />
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-[11px]">Split at: {fmt(splitAt)}</Label>
                    <input type="range" min={selected.trimStart || 0} max={selected.trimEnd || selected.duration} step={0.1} value={splitAt}
                      onChange={(e) => setSplitAt(Number(e.target.value))} className="w-full accent-indigo-600" data-testid="timeline-split-at" />
                  </div>
                  <Button size="sm" variant="outline" onClick={splitClip} data-testid="timeline-split-btn"><Scissors className="w-3.5 h-3.5 mr-1" /> Split</Button>
                </div>
              </div>
            )}

            {/* B-roll overlay track */}
            {clips.length > 0 && (
              <div className="rounded-lg border border-zinc-200 p-3 space-y-3" data-testid="broll-panel">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5"><Layers className="w-4 h-4 text-violet-600" /> B-roll overlay <span className="text-[10px] font-normal text-zinc-400">cutaway · A-roll audio continues</span></Label>
                  <label className="inline-flex items-center text-xs px-2.5 py-1 rounded-md border border-zinc-200 cursor-pointer hover:border-zinc-300" data-testid="broll-upload-label">
                    <Upload className="w-3.5 h-3.5 mr-1" /> Add B-roll
                    <input type="file" accept="video/*" className="hidden" onChange={onBrollUpload} data-testid="broll-upload-input" />
                  </label>
                </div>
                {broll.length === 0 ? (
                  <p className="text-[11px] text-zinc-400">Add overlay clips that cover the main video for a window of time (great for visual cutaways).</p>
                ) : broll.map((b) => (
                  <div key={b.id} className="rounded-md bg-zinc-50 border border-zinc-100 p-2 space-y-2" data-testid={`broll-${b.id}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] font-medium text-zinc-700 truncate">{b.name}</p>
                      <Button size="sm" variant="ghost" className="h-6 px-1.5 text-red-600 hover:bg-red-50" onClick={() => removeBroll(b.id)} data-testid={`broll-remove-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[10px]">
                      <div>
                        <Label className="text-[10px]">Start: {fmt(b.start)}</Label>
                        <input type="range" min={0} max={Math.max(0.1, totalDuration)} step={0.1} value={b.start}
                          onChange={(e) => updateBroll(b.id, { start: Number(e.target.value) })} className="w-full accent-violet-600" data-testid={`broll-start-${b.id}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">In: {fmt(b.trimStart)}</Label>
                        <input type="range" min={0} max={b.duration} step={0.1} value={b.trimStart}
                          onChange={(e) => updateBroll(b.id, { trimStart: Math.min(Number(e.target.value), b.trimEnd - 0.2) })} className="w-full accent-violet-600" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Out: {fmt(b.trimEnd)}</Label>
                        <input type="range" min={0} max={b.duration} step={0.1} value={b.trimEnd}
                          onChange={(e) => updateBroll(b.id, { trimEnd: Math.max(Number(e.target.value), b.trimStart + 0.2) })} className="w-full accent-violet-600" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Caption the assembled video */}
            {clips.length > 0 && (
              <div className="rounded-lg border border-zinc-200 p-3 space-y-2" data-testid="timeline-captions-panel">
                <label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
                  <input type="checkbox" checked={captionTimeline} onChange={(e) => setCaptionTimeline(e.target.checked)} className="accent-indigo-600" data-testid="timeline-caption-toggle" />
                  <Captions className="w-4 h-4 text-indigo-600" /> Caption the finished video (auto-transcribe + burn in)
                </label>
                {captionTimeline && (
                  <Select value={timelinePreset} onValueChange={setTimelinePreset}>
                    <SelectTrigger className="h-8 text-xs" data-testid="timeline-caption-preset"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CAPTION_PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Preview */}
            {clips.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {!previewing ? (
                    <Button size="sm" variant="outline" onClick={preview} data-testid="timeline-preview-btn"><Play className="w-3.5 h-3.5 mr-1" /> Preview</Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => { stopPreviewRef.current = true; }} data-testid="timeline-stop-preview"><Square className="w-3.5 h-3.5 mr-1" /> Stop</Button>
                  )}
                  <span className="text-[11px] text-zinc-400">Silent preview · audio is included in the final render</span>
                </div>
                <canvas ref={previewCanvasRef} className="w-full max-h-[300px] rounded-lg bg-black object-contain" data-testid="timeline-preview-canvas" />
              </div>
            )}

            {/* Output */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px]">Export format</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger data-testid="timeline-format"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(FORMAT_DIMS).map((f) => <SelectItem key={f} value={f}>{f}{f === '9:16' ? ' (Reels/TikTok)' : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">Attach to campaign post</Label>
                <Select value={targetPostId || '__none__'} onValueChange={(v) => setTargetPostId(v === '__none__' ? '' : v)}>
                  <SelectTrigger data-testid="timeline-attach-picker"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__none__">Save to library only</SelectItem>
                    {posts.map((p) => <SelectItem key={p.id} value={p.id}>{(p.post_title || p.topic || 'Untitled').slice(0, 38)} · {p.platform}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button onClick={finish} disabled={busy || clips.length === 0} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="timeline-render-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
              {targetPostId ? 'Render & attach to post' : 'Render & save to library'}
            </Button>
            {busy && busyMsg && <p className="text-[11px] text-zinc-500 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> {busyMsg}</p>}
            <p className="text-[11px] text-zinc-400">Renders sequentially in your browser (≈ the total clip length). Keep this tab open until it finishes.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VideoTimelineModal;
